import { Injectable, inject } from '@angular/core';
import { IFilterService } from 'angular';
import _ from 'lodash';
import { currencyFloatToInteger, integerToCurrencyFloat } from '@acme/b2b/pricing';

import { CalendarService } from '../calendar/calendar.service';
import { HelpersService } from '../helpers/helpers.service';
import { PermissionsService } from '../permissions/permissions.service';
import { FILTER_PORT, IRootScopePort, ROOT_SCOPE_PORT } from '../utils';

/**
 * MembershipService (Angular 18)
 *
 * Ported 1:1 from apps/b2b-ui/common/services/membership/membership.service.js
 * (legacy AngularJS name 'MembershipService'). Holds the in-memory membership
 * model plus a large set of pure helpers for sales channels, purchase-type
 * lookups, offering summaries and term/price formatting.
 *
 * Dependency mapping:
 *  - HelpersService / CalendarService / PermissionsService → Angular 18
 *    (injected directly).
 *  - `$filter` → FILTER_PORT, `$rootScope` → ROOT_SCOPE_PORT
 *    (shared fundamental ports in core/utils, wired in core.module.ts).
 *  - `angular.copy` → lodash `cloneDeep`; `angular.forEach` → native iteration.
 *
 * The single broadcast side-effect (`MembershipService::Update` on setModel)
 * is preserved exactly. Registered back into AngularJS under 'MembershipService'
 * via downgradeInjectable in app/upgrade/ng-downgraded-components.ts. Wave 3 #28.
 */
@Injectable({ providedIn: 'root' })
export class MembershipService {
  private helpersService = inject(HelpersService);
  private calendarService = inject(CalendarService);
  private permissionsService = inject(PermissionsService);
  private $filter = inject<IFilterService>(FILTER_PORT);
  private $rootScope = inject<IRootScopePort>(ROOT_SCOPE_PORT);

  model: any = {};

  // This is an annoying hack, avoids API calls, but likely error prone
  purchaseTerm: any = false;

  selectedPrimaryPhoto: any;

  salesChannels = [
    { name: 'Web', channel: 'online', enabled: true },
    { name: 'Point of Sale', channel: 'pointOfSale', enabled: true },
    { name: 'Reseller', channel: 'reseller', enabled: true },
    { name: 'Inside Sales Individual', channel: 'insideSalesIndividual', enabled: true },
    { name: 'Customer Rep', channel: 'customerRep', enabled: true },
    { name: 'Manual Entry', channel: 'manualEntry', enabled: true },
    { name: 'SalesForce', channel: 'salesforce', enabled: true },
    { name: 'Kiosk', channel: 'kiosk', enabled: true },
  ];

  membershipLevelGroups = [
    { name: 'individualAndFamily', label: 'Individual & Family' },
    { name: 'organization', label: 'Organizations' },
  ];

  purchaseTypes: Record<string, { name: string; label: string; action: string }> = {
    new_memberships: { name: 'MembershipPurchase', label: 'New Purchase', action: 'NEW_MEMBERSHIPS' },
    upgrade: { name: 'MembershipUpgrade', label: 'Upgrade', action: 'UPGRADE' },
    renew: { name: 'MembershipRenewal', label: 'Renew', action: 'RENEW' },
    postTermRenew: { name: 'MembershipPostTermRenewal', label: 'Renew (After term)', action: 'AFTER_TERM_RENEW' },
    rejoin: { name: 'MembershipRejoin', label: 'Rejoin', action: 'REJOIN' },
    renewUpgrade: { name: 'MembershipRenewalUpgrade', label: 'Renew (Upgrade)', action: 'RENEW' },
    postTermRenewUpgrade: {
      name: 'MembershipPostTermRenewalUpgrade',
      label: 'Renew (After term upgrade)',
      action: 'AFTER_TERM_RENEW',
    },
    rejoinUpgrade: { name: 'MembershipRejoinUpgrade', label: 'Rejoin (Upgrade)', action: 'REJOIN' },
    renewDowngrade: { name: 'MembershipRenewalDowngrade', label: 'Renew (Downgrade)', action: 'RENEW' },
    postTermRenewDowngrade: {
      name: 'MembershipPostTermRenewalDowngrade',
      label: 'Renew (After term downgrade)',
      action: 'AFTER_TERM_RENEW',
    },
    rejoinDowngrade: { name: 'MembershipRejoinDowngrade', label: 'Rejoin (Downgrade)', action: 'REJOIN' },
    downgrade: { name: 'MembershipDowngrade', label: 'Downgrade', action: 'DOWNGRADE' },
    cancel: { name: 'MembershipCancel', label: 'Cancel', action: 'CANCELLATION' },
  };

  purchaseTypeDisplay: Record<string, string> = {
    new_memberships: 'MembershipPurchase',
    upgrade: 'MembershipUpgrade',
    renew: 'MembershipRenewal',
    postTermRenew: 'MembershipPostTermRenewal',
    rejoin: 'MembershipRejoin',
    renewUpgrade: 'MembershipRenewalUpgrade',
    postTermRenewUpgrade: 'MembershipPostTermRenewalUpgrade',
    rejoinUpgrade: 'MembershipRejoinUpgrade',
    renewDowngrade: 'MembershipRenewalDowngrade',
    postTermRenewDowngrade: 'MembershipPostTermRenewalDowngrade',
    rejoinDowngrade: 'MembershipRejoinDowngrade',
    downgrade: 'MembershipDowngrade',
    cancel: 'MembershipCancel',
  };

  renewPurchaseTypes = [
    'MembershipRenewal',
    'MembershipPostTermRenewal',
    'MembershipRejoin',
    'MembershipRenewalUpgrade',
    'MembershipPostTermRenewalUpgrade',
    'MembershipRejoinUpgrade',
    'MembershipRenewalDowngrade',
    'MembershipPostTermRenewalDowngrade',
    'MembershipRejoinDowngrade',
  ];

  upgradeDowngradePurchaseTypes = ['MembershipUpgrade', 'MembershipDowngrade'];

  rejoinPurchaseTypes = [
    'MembershipPostTermRenewal',
    'MembershipRejoin',
    'MembershipPostTermRenewalUpgrade',
    'MembershipRejoinUpgrade',
    'MembershipPostTermRenewalDowngrade',
    'MembershipRejoinDowngrade',
  ];

  init(membership?: any): void {
    if (membership) {
      this.setModel(membership);
    } else {
      this.resetModel();
    }
  }

  setModel(model: any): void {
    model = model || {};
    if (!this.validateSalesChannels(model)) {
      model.salesChannels = _.cloneDeep(this.salesChannels);
    }
    if (!this.validateImages(model)) {
      model.images = [];
    }
    this.model = model;
    this.$rootScope.$broadcast('MembershipService::Update');
  }

  getModel(): any {
    // Seed ticket details in case it does not exist
    //this.getCategoryDetails();
    return this.model;
  }

  resetModel(): void {
    this.setModel({
      name: '',
      description: '',
      shortDescription: '',
      state: 'draft',
      salesChannels: _.cloneDeep(this.salesChannels),
      benefits: [],
      offerings: [],
      rules: {
        numberOfCardHolders: 2,
        canGift: false,
        startBeginningOfMonth: false,
        expireEndOfMonth: false,
      },
      images: [],
      termsAndConditions: '',
    });
  }

  hasMembershipMapping(): boolean {
    return this.model.types instanceof Array && this.model.types[0];
  }

  getMembershipMapping(): any[] {
    return this.hasMembershipMapping() ? this.model.types : [];
  }

  setMembershipMapping(memberships: any, defaultType?: any): void {
    this.model.types = memberships;

    //TODO: change this so that the actual primary membership mapping is set in the UI!!!
    if (defaultType) {
      this.model.defaultType = {
        program: defaultType.program,
        category: defaultType.category,
        subcategory: defaultType.subcategory,
        id: defaultType.id,
      };
    } else if (this.model.types instanceof Array && this.model.types.length == 1) {
      this.model.defaultType = {
        program: this.model.types[0].program,
        category: this.model.types[0].category,
        subcategory: this.model.types[0].subcategory,
        id: this.model.types[0].id,
      };
    } else if (this.model.types instanceof Array && this.model.types.length < 1) {
      this.model.defaultType = null;
    }
  }

  generateConfirmationEmailRestrictions(): any[] {
    let i, ii;
    const r: any[] = [];
    const salesChannels = ['online', 'pointOfSale', 'insideSalesIndividual', 'salesforce', 'customerRep', 'kiosk'];

    for (i = 0, ii = salesChannels.length; i < ii; i++) {
      r.push({
        saleChannel: salesChannels[i],
        canSendConfirmationEmail: true,
      });
    }

    return r;
  }

  createOfferingAction(): any {
    return {
      action: '',
      ruleIds: [],
      salesChannels: _.cloneDeep(this.salesChannels),
      confirmationEmailRestrictions: this.generateConfirmationEmailRestrictions(),
      printMembershipCards: true,
      includeDigitalMemberCardsInConfirmationEmail: true,
      confirmationMessage: '',
    };
  }

  getReviewState(): any {
    return this.model.state;
  }

  setReviewState(state: any): void {
    this.model.state = state;
  }

  findSalesChannel(salesChannels: any[], channel: string): any {
    let i, ii;
    let r: any = false;

    for (i = 0, ii = salesChannels.length; i < ii; i++) {
      if (salesChannels[i].channel === channel) {
        r = {
          salesChannel: salesChannels[i],
          index: i,
        };
        // Special case for membership
        if (channel === 'insideSalesIndividual') {
          r.salesChannel.label = 'Backoffice (B2B)';
        } else if (channel === 'online') {
          r.salesChannel.label = 'Consumer Web (B2C)';
        } else if (channel === 'pointOfSale') {
          r.salesChannel.label = 'Point of Sale (POS)';
        } else if (channel === 'salesforce') {
          r.salesChannel.label = 'Salesforce';
        } else if (channel === 'customerRep') {
          r.salesChannel.label = 'Customer Rep. (CSR)';
        } else if (channel === 'manualEntry') {
          r.salesChannel.label = 'Manual Entry';
        } else if (channel === 'reseller') {
          r.salesChannel.label = 'Reseller';
        } else if (channel === 'kiosk') {
          r.salesChannel.label = 'Kiosk';
        } else if (channel === 'collaborator') {
          r.salesChannel.label = 'Collaborator';
        }
        break;
      }
    }

    return r;
  }

  getMembershipLevelGroups(): any[] {
    return _.cloneDeep(this.membershipLevelGroups);
  }

  getSalesChannels(channels: any[]): string {
    let i, ii;
    const _channels: any[] = [];

    for (i = 0, ii = channels.length; i < ii; i++) {
      if (channels[i].enabled) {
        _channels.push(channels[i].label);
      }
    }

    return _channels.length === channels.length ? 'All' : _channels.join(', ');
  }

  orderSalesChannels(salesChannels: any): any {
    let i, ii;
    const channels: any[] = [];
    const _channels: any[] = [];
    const order = [
      'online',
      'pointOfSale',
      'individualInsideSales',
      'salesforce',
      'reseller',
      'customerRep',
      'manualEntry',
    ];

    if (salesChannels instanceof Array) {
      for (i = 0, ii = salesChannels.length; i < ii; i++) {
        if (order.length && order[0] == salesChannels[i].channel) {
          channels.push(salesChannels[i]);
          order.shift();
        } else {
          _channels.push(salesChannels[i]);
        }
      }

      salesChannels = channels.concat(_channels);
    }

    return salesChannels;
  }

  validateSalesChannels(membership: any): boolean {
    return membership && membership.salesChannels instanceof Array;
  }

  validateImages(membership: any): boolean {
    return membership && membership.images instanceof Array;
  }

  getPrimaryPhoto(): any {
    let i, ii;
    if (this.model.images instanceof Array) {
      for (i = 0, ii = this.model.images.length; i < ii; i++) {
        if (this.model.images[i].id === this.selectedPrimaryPhoto) {
          return this.model.images[i].screen;
        }
      }
    }
  }

  hasImages(): boolean {
    return this.model.images instanceof Array;
  }

  hasPrice(): any {
    return this.model.price;
  }

  getPurchaseType(itemType: string): string {
    let type;
    let r = '';

    for (type in this.purchaseTypes) {
      if (Object.prototype.hasOwnProperty.call(this.purchaseTypes, type)) {
        if (type === itemType) {
          r = this.purchaseTypes[type].name;
        }
      }
    }

    return r;
  }

  getActionFromPurchaseType(purchaseType: string): string {
    let type;
    let r = '';

    for (type in this.purchaseTypes) {
      if (Object.prototype.hasOwnProperty.call(this.purchaseTypes, type)) {
        if (this.purchaseTypes[type].name === purchaseType) {
          r = type;
        }
      }
    }

    return r;
  }

  getEmailActionFromPurchaseType(purchaseType: string): string {
    let type;
    let r = '';

    for (type in this.purchaseTypes) {
      if (Object.prototype.hasOwnProperty.call(this.purchaseTypes, type)) {
        if (this.purchaseTypes[type].name === purchaseType) {
          r = this.purchaseTypes[type].action;
        }
      }
    }

    return r;
  }

  hasSingleOffering(offerings: any[]): boolean {
    let r = false;

    if (offerings.length === 1) {
      r = true;
    }

    return r;
  }

  hasSinglePricepoint(offerings: any[]): boolean {
    let r = false;

    if (
      offerings.length === 1 &&
      offerings[0].priceLists instanceof Array &&
      offerings[0].priceLists.length === 1 &&
      offerings[0].priceLists[0].prices instanceof Array &&
      offerings[0].priceLists[0].prices.length <= 1
    ) {
      r = true;
    }

    return r;
  }

  getFirstPricepoint(offering: any): any {
    let r = null;

    if (
      offering.priceLists instanceof Array &&
      offering.priceLists.length === 1 &&
      offering.priceLists[0].prices instanceof Array &&
      offering.priceLists[0].prices.length
    ) {
      r = offering.priceLists[0].prices[0];
    }

    return r;
  }

  getMembershipDuration(offerings: any): any {
    let r;

    if (offerings instanceof Array && offerings.length) {
      if (offerings.length > 1) {
        r = 'Varies';
      } else {
        if (offerings[0].duration) {
          r = this.helpersService.getFormattedDuration(offerings[0].duration);
        }
      }
    }

    return r;
  }

  getMembershipTerm(offerings: any): string {
    const r: any[] = [];

    if (offerings instanceof Array && offerings.length) {
      if (offerings.length > 1) {
        r.push('Varies');
      } else {
        if (offerings[0].membershipStartDate) {
          r.push(
            this.$filter('date')(
              this.calendarService.parseDate(offerings[0].membershipStartDate),
              this.helpersService.dateFormats.standardDate,
            ),
          );
        } else {
          r.push(this.$filter('date')(new Date(), this.helpersService.dateFormats.standardDate));
        }

        if (offerings[0].membershipExpirationDate) {
          r.push(
            this.$filter('date')(
              this.calendarService.parseDate(offerings[0].membershipExpirationDate),
              this.helpersService.dateFormats.standardDate,
            ),
          );
        } else {
          if (offerings[0].duration) {
            const currentDate = new Date();
            r.push(
              this.$filter('date')(
                currentDate.setMonth(currentDate.getMonth() + offerings[0].duration),
                this.helpersService.dateFormats.standardDate,
              ),
            );
          }
        }
      }
    }
    return r.join(' - ');
  }

  getMembershipTermFromStartEnd(start: any, end: any): string {
    return [
      this.$filter('date')(this.calendarService.parseDate(start), this.helpersService.dateFormats.standardDate),
      this.$filter('date')(this.calendarService.parseDate(end), this.helpersService.dateFormats.standardDate),
    ].join(' - ');
  }

  getOfferingSummary(offerings: any[]): string {
    let r = '';

    if (this.hasSingleOffering(offerings)) {
      if (this.getFirstPricepoint(offerings[0])) {
        r = offerings[0].name;
      } else {
        r = 'Not available';
      }
    } else {
      r = offerings.length + ' Offerings';
    }

    return r;
  }

  getPriceRange(offerings: any[]): string {
    let prices: any[] = [];
    let minPrice: number | null = null;
    let maxPrice: number | null = null;
    const priceRange: any[] = [];

    offerings.forEach((offering) => {
      if (
        offering.priceLists instanceof Array &&
        offering.priceLists.length &&
        offering.priceLists[0].prices instanceof Array &&
        offering.priceLists[0].prices.length
      ) {
        prices = offering.priceLists[0].prices;

        if (minPrice === null) {
          minPrice = currencyFloatToInteger(prices[0].discountedPrice || 0, { significant: 2 });
        }
        if (maxPrice === null) {
          maxPrice = currencyFloatToInteger(prices[0].discountedPrice || 0, { significant: 2 });
        }

        prices.forEach((price) => {
          minPrice = Math.min(minPrice as number, currencyFloatToInteger(price.discountedPrice, { significant: 2 }));
          maxPrice = Math.max(maxPrice as number, currencyFloatToInteger(price.discountedPrice, { significant: 2 }));
        });
      }
    });

    if (minPrice !== null) {
      priceRange.push(this.$filter('currency')(integerToCurrencyFloat(minPrice, { significant: 2 })));
    }

    if (maxPrice !== null && maxPrice !== minPrice) {
      priceRange.push(this.$filter('currency')(integerToCurrencyFloat(maxPrice, { significant: 2 })));
    }

    return priceRange.join(' - ');
  }

  getNumOfCardHolders(rules: any): string {
    if (rules && rules.numberOfCardHolders != null) {
      return rules.numberOfCardHolders.toString();
    }

    return '1';
  }

  getAuxGroup(membershipLevelCollection: any): any {
    if (membershipLevelCollection instanceof Array) {
      return membershipLevelCollection[0].name;
    }
    return null;
  }

  /**
   * @note Hide Aux Memberships from the list for New Membership Sales
   * @link https://acmeticketing.atlassian.net/browse/ENG-11091
   *
   * @param {{ auxiliary: boolean; offerings: Array }} level
   * @param {string} purchaseType
   */
  filterLevelOfferingsByPurchaseType(level: any, purchaseType: string): any[] {
    const offerings = Array.isArray(level.offerings) ? level.offerings : [];
    const isAuxiliary = level.auxiliary === true;

    return offerings.filter((offering: any) => {
      const actions = Array.isArray(offering.actions) ? offering.actions : [];
      const selectedAction = actions.find((action: any) => {
        return purchaseType.includes(action.action);
      });

      const salesChannels =
        selectedAction && Array.isArray(selectedAction.salesChannels) ? selectedAction.salesChannels : [];

      const channelRule = salesChannels.find((salesChannel: any) => {
        return salesChannel.channel === 'insideSalesIndividual';
      });

      if (channelRule.enabled !== true) {
        return false;
      }

      switch (purchaseType) {
        case this.purchaseTypes['new_memberships'].action:
          return isAuxiliary === false;

        default:
          return true;
      }
    });
  }

  /**
   * Only applies filtering if purchaseType is defined
   * @param {*} levels Membership eligibility levels
   * @param {string | undefined} purchaseType Action name
   * @returns {Array<any>}
   */
  flattenLevelByOfferings(levels: any, purchaseType?: string): any[] {
    let list: any[] = [];

    (levels || []).forEach((level: any) => {
      // Based on the purchase type we filter out offerings that are not available to this sales channel
      const filteredOfferings = purchaseType
        ? this.filterLevelOfferingsByPurchaseType(level, purchaseType)
        : level.offerings;

      // If we have a non-empty offering list, we assign that to the level and flatten
      if (filteredOfferings.length > 0) {
        level.offerings = filteredOfferings;
        list = list.concat([
          {
            membershipLevelName: level.name,
            offerings: this.getOfferingSummary(level.offerings),
            type: level.purchaseTypeLabel,
            numOfOfferings: level.offerings.length,
            term: this.getMembershipTerm(level.offerings),
            duration: this.getMembershipDuration(level.offerings),
            cardHolders: this.getNumOfCardHolders(level.rules),
            priceRange: this.getPriceRange(level.offerings),
            level: level,
            auxGroup: this.getAuxGroup(level.membershipLevelCollection),
            hasSinglePricepoint: this.hasSinglePricepoint(level.offerings),
          },
        ]);
      }
    });

    return list;
  }

  /**
   * Item type should be the name property of purchaseTypes
   *
   * @param itemType
   * @returns {string}
   */
  getLifeCycleLabel(itemType: string): string {
    const r = this.getMembershipTypeLabelByName(itemType);
    return 'Membership' + (r ? ' ' + r : '');
  }

  getMembershipTypeLabel(itemType: string): string {
    let type;
    let r = '';

    for (type in this.purchaseTypes) {
      if (Object.prototype.hasOwnProperty.call(this.purchaseTypes, type)) {
        if (type === itemType) {
          r = this.purchaseTypes[type].label;
        }
      }
    }

    return r;
  }

  getMembershipTypeLabelByName(name: string): string {
    let type;
    let r = '';

    for (type in this.purchaseTypes) {
      if (Object.prototype.hasOwnProperty.call(this.purchaseTypes, type)) {
        if (this.purchaseTypes[type].name === name) {
          r = this.purchaseTypes[type].label;
        }
      }
    }

    return r;
  }

  checkSendConfirmationForChannel(confirmationEmailRestrictions: any, saleChannel: any): boolean {
    let r = false;
    let i, ii;

    confirmationEmailRestrictions = confirmationEmailRestrictions instanceof Array ? confirmationEmailRestrictions : [];

    if (saleChannel) {
      for (i = 0, ii = confirmationEmailRestrictions.length; i < ii; i++) {
        if (
          confirmationEmailRestrictions[i] &&
          Object.prototype.hasOwnProperty.call(confirmationEmailRestrictions[i], 'saleChannel') &&
          confirmationEmailRestrictions[i].saleChannel === saleChannel &&
          confirmationEmailRestrictions[i].canSendConfirmationEmail
        ) {
          r = true;
        }
      }
    }
    return r;
  }

  canSendMemberCardsForAction(membershipOffering: any, action: any): boolean {
    let r = false;
    let i, ii;
    const isCSR = this.permissionsService.check('CSR');
    const channel = isCSR ? 'customerRep' : 'insideSalesIndividual';

    if (membershipOffering && action && membershipOffering.actions instanceof Array) {
      for (i = 0, ii = membershipOffering.actions.length; i < ii; i++) {
        if (
          membershipOffering.actions[i].action &&
          membershipOffering.actions[i].action.toUpperCase() === action.toUpperCase()
        ) {
          r = this.checkSendConfirmationForChannel(
            membershipOffering.actions[i].confirmationEmailRestrictions,
            channel,
          );
        }
      }
    }
    return r;
  }

  isRenewal(membership: any): boolean {
    return this.renewPurchaseTypes.includes(membership.itemType);
  }

  isUpgradeDowngrade(membership: any): boolean {
    return this.upgradeDowngradePurchaseTypes.includes(membership.itemType);
  }
}
